import { action, makeObservable, observable } from 'mobx';

import { concatPath } from '@feathr/hooks';
import type {
  Attributes,
  IBaseAttributes,
  IListParams,
  IRachisMessage,
  ListResponse,
  TConstraints,
} from '@feathr/rachis';
import { Collection, DisplayModel, isWretchError, wretch } from '@feathr/rachis';

import type { IForm, IFormBaseStats, IFormConfig } from '../forms';
import { DonationFormConfigManager } from './donation_forms.helpers';

// Constants for field names and validation types to reduce magic strings
export const FIELD_NAMES = {
  CUSTOM_AMOUNT: 'custom-amount',
  DONATION_AMOUNT: 'donation-amount',
  MONTHLY_DONATION_AMOUNT: 'recurring-donation-amount',
} as const;

const VALIDATION_TYPES = {
  MIN_VALUE: 'minValue',
} as const;

export interface IExportDonationsProps {
  email: string;
  start?: string;
  end?: string;
  form_ids?: string[];
  fields: string[];
}

export interface IDonationFormStats extends IFormBaseStats {
  /** Average dollar amount of donations raised. */
  average_donation: number;
  /** Percentage of donations that included a tip. */
  credits_rate: number;
  /** Total number of crumbs with donation flavor associated with this form. */
  donations_count: number;
  /** Total dollar amount of tips raised. */
  donations_credits: number;
  /** Total number of donations with a tip */
  donations_credits_count: number;
  /** Total number of recurring donations */
  donations_recurring_count: number;
  /** Total dollar amount of recurring donations */
  donations_recurring_total: number;
  /** Total dollar amount of donations raised. */
  donations_total: number;
  /** Total amount of growth credits earned */
  growth_credits: number;
}

export interface IDonationFormConfig extends Omit<IFormConfig, 'rows'> {
  readonly account_id: string;
  fields: Record<string, Record<string, unknown>>;
  allowMonthlyDonations?: boolean;
  allowDescriptions?: boolean;
}

// Using intersection type to properly override config while maintaining IBaseAttributes compatibility
export interface IDonationForm extends Omit<IForm, 'config' | 'stats'>, IBaseAttributes {
  kind: 'donation';
  readonly stats: IDonationFormStats;
  config: IDonationFormConfig;
}

/**
 * Pseudoclass around Forms to make Donation Forms easier to interact with.
 */
export class DonationForm extends DisplayModel<IDonationForm> {
  public readonly className = 'Form';
  public override collection: DonationForms<this> | null = null;
  @observable private _configManager: DonationFormConfigManager | null = null;
  // Keep a simple signature of the last raw config used to build the manager
  private _configSignature: string | null = null;

  constructor(attributes?: Partial<Attributes<DonationForm>>) {
    super(attributes);
    makeObservable(this);
  }

  // TODO: implement constraintssss :(
  public get constraints(): TConstraints<IDonationForm> {
    return {};
  }

  public get name(): string {
    return this.get('name', '').trim() || 'Unnamed Donation Form';
  }

  public getItemUrl(pathSuffix?: string): string {
    return concatPath(`/fundraise/${this.id}`, pathSuffix);
  }

  /**
   * Returns the reactive config manager for nested config manipulation.
   * Lazily initialized on first access.
   */
  public get configManager(): DonationFormConfigManager {
    const rawConfig = this.get('config', {} as IDonationFormConfig);
    const rawSignature = JSON.stringify(rawConfig ?? {});

    // Initialize if missing
    if (!this._configManager) {
      this._configManager = new DonationFormConfigManager(rawConfig);
      this._configSignature = rawSignature;
      return this._configManager;
    }

    // Rehydrate if the manager was created before data arrived (empty fields)
    // and the fetched config now has fields.
    const managerHasFields = this._configManager.fields.size > 0;
    const rawHasFields = !!rawConfig?.fields && Object.keys(rawConfig.fields).length > 0;
    if (!managerHasFields && rawHasFields) {
      this._configManager = new DonationFormConfigManager(rawConfig);
      this._configSignature = rawSignature;
      return this._configManager;
    }

    // If the raw config reference/content changed and the manager is not dirty,
    // rebuild the manager to avoid stale config captured during pending.
    if (!this._configManager.isDirty && this._configSignature !== rawSignature) {
      this._configManager = new DonationFormConfigManager(rawConfig);
      this._configSignature = rawSignature;
    }

    return this._configManager;
  }

  /**
   * Returns the config object. For backward compatibility.
   * Use configManager for reactive nested updates.
   */
  public get config(): IDonationFormConfig {
    // If configManager exists, return its JSON representation
    if (this._configManager) {
      return this._configManager.toJSON();
    }
    return this.get('config', {} as IDonationFormConfig);
  }

  /**
   * Override isDirty to include configManager dirty state.
   * This ensures the model appears dirty when nested config changes occur.
   */
  public override get isDirty(): boolean {
    const baseIsDirty = super.isDirty;
    const configManagerIsDirty = this._configManager?.isDirty ?? false;
    return baseIsDirty || configManagerIsDirty;
  }

  /**
   * Override patchDirty to sync configManager state before saving.
   */
  @action
  public override async patchDirty(): Promise<this> {
    // Sync configManager to model attributes before saving
    if (this._configManager?.isDirty) {
      this.set({ config: this._configManager.toJSON() });
    }
    const result = await super.patchDirty();

    // Rebuild config manager from server response after successful save
    // This ensures the manager is perfectly in sync with the server state
    if (!result.isErrored && this._configManager) {
      const rawConfig = this.get('config', {} as IDonationFormConfig);
      this._configManager = new DonationFormConfigManager(rawConfig);
      this._configSignature = JSON.stringify(rawConfig ?? {});
    }

    return result;
  }

  public get minimumAmount(): number | undefined {
    const customAmountField = this.configManager.getField(FIELD_NAMES.CUSTOM_AMOUNT);
    if (!customAmountField) {
      return undefined;
    }

    const minValueRule = customAmountField.validation.find(
      (rule) => rule.type === VALIDATION_TYPES.MIN_VALUE,
    );

    return minValueRule ? Number(minValueRule.value) : undefined;
  }

  @action
  public clearMinimumAmount(): void {
    const customAmountField = this.configManager.getOrCreateField(FIELD_NAMES.CUSTOM_AMOUNT);
    customAmountField.removeValidationByType(VALIDATION_TYPES.MIN_VALUE);
  }

  /**
   * Sets the minimum donation amount for the custom amount field.
   * Creates the custom-amount field if it doesn't exist.
   *
   * @param amount - The minimum amount (must be >= 0)
   * @throws Error if amount is negative
   */
  @action
  public setMinimumAmount(amount: number): void {
    if (amount < 0) {
      throw new Error('Minimum donation amount cannot be negative');
    }

    const customAmountField = this.configManager.getOrCreateField(FIELD_NAMES.CUSTOM_AMOUNT);

    // Remove any existing minValue validation rules and add the new one
    customAmountField.removeValidationByType(VALIDATION_TYPES.MIN_VALUE);
    customAmountField.addValidation({
      value: String(amount),
      type: VALIDATION_TYPES.MIN_VALUE,
      message: `Minimum donation is $${amount}`,
    });
  }
}

export class DonationForms<Model extends DonationForm = DonationForm> extends Collection<Model> {
  public getClassName(): string {
    return 'forms';
  }

  public getModel(attributes: Partial<Attributes<Model>>): Model {
    return new DonationForm(attributes) as Model;
  }

  // Override create to ensure kind is set to 'donation'
  public override create(attributes?: Partial<Exclude<IDonationForm, 'kind'>>): Model {
    return super.create({ kind: 'donation', ...attributes } as Partial<Attributes<Model>>);
  }

  // Overriding list prevents "kind" needing to be passed in for queries.
  public override list(params?: IListParams<Attributes<Model>>): ListResponse<Model> {
    return super.list({ filters: { kind: 'donation', ...params?.filters }, ...params });
  }

  public override url(variant: 'export'): string;
  public override url(): string;
  public override url(variant?: string): string {
    const root = super.url();

    if (variant === 'export') {
      return `${this.getHostname()}donation/export`;
    }

    return root;
  }

  public async exportDonations(props: IExportDonationsProps): Promise<IRachisMessage> {
    const response = await wretch<IRachisMessage>(this.url('export'), {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(props),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }
}
